import React, { useCallback, useEffect, useState } from "react";

import { ApiTareasPersonal, ListaDeTareas, TareaDeLista } from "../../../../services/v2/classPortal.service";
import { fechaCorta, Icono, mensajeDeError, useAlVolver } from "../comun";
import { IconoEntrega, puntosTxt, textoEntrega } from "./comunTareas";
import "./tareas.css";

/*
	Pestaña Tareas del portal (personal, Parte B): las tareas de la clase por unidad, con filtros (Todas / Por Calificar /
	Programadas / Borradores) y sus conteos. Tocar una tarea abre Calificar; el lápiz abre Nueva Tarea (solo las propias del
	horario: las que bajan del curso o del módulo se cambian desde Contenido). Carga al abrir y al volver a la pestaña.
*/

type Filtro = "todas" | "porCalificar" | "programadas" | "borradores"

const FASE: Record<string, string> = { en_curso: "En Curso", terminada: "Terminada", proxima: "Próxima" }

const pasa = (t: TareaDeLista, f: Filtro): boolean => {
	if (f === "todas") return true
	if (f === "programadas") return t.status === "programada"
	if (f === "borradores") return t.status === "borrador"
	return (t.gradable || t.delivery === "quiz") && t.status === "publicada" && t.counts.porCalificar > 0
}

const FilaTarea: React.FC<{ t: TareaDeLista; onAbrir: (t: TareaDeLista) => void; onEditar: (t: TareaDeLista) => void }> = ({ t, onAbrir, onEditar }) => {
	const c = t.counts
	const sub = [textoEntrega(t.delivery) + (t.delivery === "foto" || t.delivery === "archivo" ? "" : ""),
		t.status === "programada" ? "Se Publica " + (t.publishLabel || "") : t.dueLabel ? (t.delivery === "en_clase" ? t.dueLabel : "Vence " + t.dueLabel) : null,
		t.delivery === "sin_entrega" ? null : puntosTxt(t.points) + " Pts"].filter(Boolean).join(" · ")
	const entregable = t.delivery === "foto" || t.delivery === "archivo" || t.delivery === "quiz"
	const publicada = t.status === "publicada"
	const conNota = t.gradable || t.delivery === "quiz"
	return (
		<div style={{ position: "relative" }}>
			<button type="button" className="tkFila" onClick={() => onAbrir(t)} aria-label={"Abrir " + t.title}>
				<span className={"tkIcono" + (publicada && conNota && c.porCalificar > 0 ? " tkIconoAcento" : "")}><IconoEntrega forma={t.delivery} /></span>
				<span className="tkFilaTextos">
					<span className="tkFilaTitulo" style={{ display: "block" }}>{t.title}</span>
					<span className="tkFilaSub" style={{ display: "block" }}>{sub}</span>
					<span style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
						{t.status === "borrador" ? <span className="pcChip pcChipOro">Borrador</span> : null}
						{t.status === "programada" ? <span className="pcChip pcChipBorde">Programada</span> : null}
						{t.closed ? <span className="pcChip pcChipOro">Cerrada</span> : null}
						{t.audience === "lista" ? <span className="pcChip pcChipBorde">Alumnos Específicos</span> : null}
						{conNota && !t.categoryOk ? <span className="pcChip pcChipOro">Sin Categoría</span> : null}
						<span className="pcChip pcChipBorde">{t.originLabel}</span>
					</span>
				</span>
				<span className="tkFilaLado" style={{ paddingRight: t.inherited ? 0 : 44 }}>
					{publicada && entregable ? <span className="tkConteo"><b>{c.entregadas}</b><span className="pcTenue pcChico">/ {c.total} Entregadas</span></span> : null}
					{publicada && conNota && c.porCalificar > 0 ? <span className="pcChip pcChipAcento">{c.porCalificar} Por Calificar</span> : null}
					{publicada && conNota && c.porCalificar === 0 && c.total > 0 && c.devueltas === c.total ? <span className="pcChip">{c.devueltas} De {c.total} Calificadas</span> : null}
					{publicada && entregable && (t.overdue || t.closed) && c.sinEntregar > 0 ? <span className="pcChip pcChipOro">{c.sinEntregar} No Entregaron</span> : null}
					{publicada && t.gradable && c.repetir > 0 ? <span className="pcChip pcChipOro">{c.repetir} Por Repetir</span> : null}
					{t.delivery === "quiz" ? <span className="pcChip pcChipBorde">Quiz</span> : null}
				</span>
			</button>
			{!t.inherited ? (
				<button type="button" className="pcIb" style={{ position: "absolute", right: 8, top: 12 }} aria-label={"Editar " + t.title} onClick={() => onEditar(t)}><Icono n="lapiz" size={18} /></button>
			) : null}
		</div>
	)
}

interface Props
{
	api: ApiTareasPersonal
	puedeCrear: boolean
	onNueva: () => void
	onNuevaUnidad: () => void
	onEditar: (postId: number) => void
	onCalificar: (postId: number) => void
	/** Parte D: un quiz abre sus Resultados. */
	onResultados?: (postId: number) => void
	onContadores?: (porCalificar: number) => void
}

const TareasPanel: React.FC<Props> = ({ api, puedeCrear, onNueva, onNuevaUnidad, onEditar, onCalificar, onResultados, onContadores }) => {
	const [datos, setDatos] = useState<ListaDeTareas | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [filtro, setFiltro] = useState<Filtro>("todas")
	const [aviso, setAviso] = useState<string | null>(null)

	const cargar = useCallback(() => {
		api.list().then((d) => { setDatos(d); setError(null); if (onContadores) onContadores(d.counts.porCalificar) }).catch((e) => setError(mensajeDeError(e, "No pudimos cargar las tareas.")))
	}, [api, onContadores])
	useEffect(() => { cargar() }, [cargar])
	useAlVolver(cargar)

	if (error) return <div className="pcAviso pcOro" role="alert">{error}</div>
	if (datos === null) return <div className="pcCargando">Cargando…</div>

	const abrir = (t: TareaDeLista) => {
		if (t.delivery === "quiz" && t.status !== "borrador" && onResultados) { onResultados(t.id); return }
		if (t.gradable && t.status !== "borrador") { onCalificar(t.id); return }
		if (!t.inherited) { onEditar(t.id); return }
		setAviso("Esta tarea no se califica aquí. Si viene del curso, su ficha se cambia en la pestaña Contenido.")
	}
	const FILTROS: [Filtro, string, number][] = [["todas", "Todas", datos.counts.all], ["porCalificar", "Por Calificar", datos.counts.porCalificar],
		["programadas", "Programadas", datos.counts.programadas], ["borradores", "Borradores", datos.counts.borradores]]
	//In "Todas" a unit without tasks still shows (a unit just created here must be visible); other filters only show what matches.
	const grupos = datos.units.map((u, i) => ({ u, i, tareas: u.tasks.filter((t) => pasa(t, filtro)) }))
		.filter((g) => g.tareas.length > 0 || (filtro === "todas" && g.u.id !== null))

	return (
		<>
			<div className="tkBarra">
				<span className="tkEspacio" />
				<button type="button" className="pcBtn pcBtnS" onClick={onNuevaUnidad}>Nueva Unidad</button>
				{puedeCrear ? <button type="button" className="pcBtn pcBtnP" onClick={onNueva}><Icono n="mas" />Nueva Tarea</button> : null}
			</div>
			<div className="tkBarra" role="group" aria-label="Filtrar tareas">
				{FILTROS.map(([id, texto, n]) => (
					<button key={id} type="button" className="tkFiltro" aria-pressed={filtro === id} onClick={() => setFiltro(id)}>{texto} · {n}</button>
				))}
				<button type="button" className="tkFiltro" style={{ marginLeft: "auto" }} onClick={cargar}>Actualizar</button>
			</div>
			{aviso ? <div className="pcAviso" role="status" style={{ color: "var(--pc-oro-texto)" }}>{aviso}</div> : null}
			{grupos.length === 0 ? <div className="pcVacio">{datos.counts.all === 0 ? "Aún No Hay Tareas" : "No Hay Tareas En Este Filtro"}</div> : null}
			{grupos.map(({ u, i, tareas }) => (
				<section key={u.id ?? "sin"} className="pcTarjeta tkUnidad" aria-label={u.name}>
					<div className="tkUnidadCabeza">
						<div>
							<div className="pcEtiqueta" style={{ fontSize: 12.5 }}>{u.id === null ? "Sin Unidad" : "Unidad " + (i + 1) + (u.phase ? " · " + FASE[u.phase] : "")}</div>
							{u.id === null ? null : <h3>{u.name}</h3>}
						</div>
						{u.startsOn || u.endsOn ? <span className="pcTenue pcChico">{[u.startsOn, u.endsOn].filter(Boolean).map((f) => fechaCorta(f as string)).join(" – ")}</span> : null}
					</div>
					{tareas.length === 0 ? <div className="pcVacio" style={{ padding: "4px 0" }}>Sin Tareas</div> : null}
					{tareas.map((t) => <FilaTarea key={t.id} t={t} onAbrir={abrir} onEditar={(x) => onEditar(x.id)} />)}
				</section>
			))}
		</>
	)
}

export default TareasPanel
